/**
 * Centralized error code definitions with icons and display text
 * Maps API ExceptionCodeEnum values to UI display definitions
 */
import type { ComponentType } from "react";
import { AlertCircle, AlertTriangle, Clock, Copy01, FileSearch02, FileX02, XCircle } from "@untitledui/icons";
import { ExceptionCodeEnum } from "@/types/api/common";

export interface ErrorCodeDefinition {
    code: string;
    displayName: string;
    icon: ComponentType<any>;
    color: "warning" | "error" | "gray";
    description: string;
}

/**
 * Map of API exception codes to UI error definitions
 * Includes icons, colors, and display names for common error types
 */
export const ERROR_CODES: Record<string, ErrorCodeDefinition> = {
    // ==========================================================================
    // Authentication Errors (1xx)
    // ==========================================================================
    [ExceptionCodeEnum.AuthTokenExpired]: {
        code: ExceptionCodeEnum.AuthTokenExpired,
        displayName: "Session Expired",
        icon: AlertCircle,
        color: "error",
        description: "Your session has expired. Please log in again.",
    },
    [ExceptionCodeEnum.AuthTokenInvalid]: {
        code: ExceptionCodeEnum.AuthTokenInvalid,
        displayName: "Authentication Failed",
        icon: AlertCircle,
        color: "error",
        description: "Your authentication token is invalid. Please log in again.",
    },
    [ExceptionCodeEnum.AuthMissingClaims]: {
        code: ExceptionCodeEnum.AuthMissingClaims,
        displayName: "Missing Permissions",
        icon: AlertTriangle,
        color: "error",
        description: "Your account is missing required permissions. Please contact support.",
    },
    [ExceptionCodeEnum.AuthSignatureInvalid]: {
        code: ExceptionCodeEnum.AuthSignatureInvalid,
        displayName: "Authentication Failed",
        icon: AlertCircle,
        color: "error",
        description: "Authentication failed. Please log in again.",
    },
    [ExceptionCodeEnum.AuthMissingCredentials]: {
        code: ExceptionCodeEnum.AuthMissingCredentials,
        displayName: "Authentication Required",
        icon: AlertCircle,
        color: "error",
        description: "Authentication credentials are missing. Please log in again.",
    },

    // ==========================================================================
    // Authorization Errors (2xx)
    // ==========================================================================
    [ExceptionCodeEnum.AuthzAccessDenied]: {
        code: ExceptionCodeEnum.AuthzAccessDenied,
        displayName: "Access Denied",
        icon: AlertTriangle,
        color: "error",
        description: "You do not have permission to perform this action.",
    },
    [ExceptionCodeEnum.AuthzEntitlementDenied]: {
        code: ExceptionCodeEnum.AuthzEntitlementDenied,
        displayName: "Feature Not Available",
        icon: AlertCircle,
        color: "warning",
        description: "This feature is not included in your current plan. Please upgrade to access this feature.",
    },
    [ExceptionCodeEnum.AuthzTenantMismatch]: {
        code: ExceptionCodeEnum.AuthzTenantMismatch,
        displayName: "Tenant Mismatch",
        icon: AlertTriangle,
        color: "error",
        description: "This resource belongs to a different tenant.",
    },
    [ExceptionCodeEnum.AuthzOrganizationMismatch]: {
        code: ExceptionCodeEnum.AuthzOrganizationMismatch,
        displayName: "Organization Mismatch",
        icon: AlertTriangle,
        color: "error",
        description: "This resource belongs to a different organization.",
    },
    [ExceptionCodeEnum.AuthzInsufficientRole]: {
        code: ExceptionCodeEnum.AuthzInsufficientRole,
        displayName: "Insufficient Permissions",
        icon: AlertTriangle,
        color: "error",
        description: "Your user role does not have permission for this action.",
    },
    [ExceptionCodeEnum.AuthzNoOrganizationAssigned]: {
        code: ExceptionCodeEnum.AuthzNoOrganizationAssigned,
        displayName: "No Organization Assigned",
        icon: AlertTriangle,
        color: "error",
        description: "You are not assigned to any organization. Please contact your system administrator.",
    },
    [ExceptionCodeEnum.AuthzClientUnresolved]: {
        code: ExceptionCodeEnum.AuthzClientUnresolved,
        displayName: "Account Configuration Error",
        icon: AlertTriangle,
        color: "error",
        description: "Your account's client could not be resolved. Please contact support.",
    },

    // ==========================================================================
    // Validation Errors (3xx)
    // ==========================================================================
    [ExceptionCodeEnum.ValFileTooLarge]: {
        code: ExceptionCodeEnum.ValFileTooLarge,
        displayName: "File Too Large",
        icon: AlertTriangle,
        color: "error",
        description: "The file size exceeds the allowed limit.",
    },
    [ExceptionCodeEnum.ValFileTypeNotEnabled]: {
        code: ExceptionCodeEnum.ValFileTypeNotEnabled,
        displayName: "Format Not Enabled",
        icon: AlertCircle,
        color: "error",
        description: "This file format is not enabled for your organization.",
    },
    [ExceptionCodeEnum.ValUnsupportedFileType]: {
        code: ExceptionCodeEnum.ValUnsupportedFileType,
        displayName: "Unsupported Format",
        icon: AlertCircle,
        color: "error",
        description: "This file type is not supported.",
    },
    [ExceptionCodeEnum.ValMissingRequiredField]: {
        code: ExceptionCodeEnum.ValMissingRequiredField,
        displayName: "Missing Information",
        icon: AlertTriangle,
        color: "warning",
        description: "Please fill in all required fields.",
    },
    [ExceptionCodeEnum.ValInvalidFormat]: {
        code: ExceptionCodeEnum.ValInvalidFormat,
        displayName: "Invalid Format",
        icon: AlertCircle,
        color: "error",
        description: "One or more fields have an invalid format.",
    },
    [ExceptionCodeEnum.ValMaxLengthExceeded]: {
        code: ExceptionCodeEnum.ValMaxLengthExceeded,
        displayName: "Input Too Long",
        icon: AlertTriangle,
        color: "error",
        description: "The input exceeds the maximum allowed length.",
    },
    [ExceptionCodeEnum.ValBelowMinimum]: {
        code: ExceptionCodeEnum.ValBelowMinimum,
        displayName: "Value Too Low",
        icon: AlertTriangle,
        color: "error",
        description: "The value is below the minimum allowed.",
    },
    [ExceptionCodeEnum.ValAboveMaximum]: {
        code: ExceptionCodeEnum.ValAboveMaximum,
        displayName: "Value Too High",
        icon: AlertTriangle,
        color: "error",
        description: "The value exceeds the maximum allowed.",
    },
    [ExceptionCodeEnum.ValInvalidId]: {
        code: ExceptionCodeEnum.ValInvalidId,
        displayName: "Invalid ID",
        icon: AlertCircle,
        color: "error",
        description: "The provided ID format is invalid.",
    },
    [ExceptionCodeEnum.ValInvalidDateTime]: {
        code: ExceptionCodeEnum.ValInvalidDateTime,
        displayName: "Invalid Date/Time",
        icon: AlertCircle,
        color: "error",
        description: "The date or time format is invalid.",
    },
    [ExceptionCodeEnum.ValInvalidEnumValue]: {
        code: ExceptionCodeEnum.ValInvalidEnumValue,
        displayName: "Invalid Selection",
        icon: AlertCircle,
        color: "error",
        description: "The selected value is not valid.",
    },
    [ExceptionCodeEnum.ValInvalidOrganizationFormat]: {
        code: ExceptionCodeEnum.ValInvalidOrganizationFormat,
        displayName: "Invalid Organization Name",
        icon: AlertCircle,
        color: "error",
        description: "The organization name format is invalid.",
    },

    // ==========================================================================
    // Not Found Errors (4xx)
    // ==========================================================================
    [ExceptionCodeEnum.NotFoundDocument]: {
        code: ExceptionCodeEnum.NotFoundDocument,
        displayName: "Document Not Found",
        icon: FileX02,
        color: "error",
        description: "The document you requested could not be found.",
    },
    [ExceptionCodeEnum.NotFoundFile]: {
        code: ExceptionCodeEnum.NotFoundFile,
        displayName: "File Not Found",
        icon: FileX02,
        color: "error",
        description: "The file you requested could not be found.",
    },
    [ExceptionCodeEnum.NotFoundBlobContent]: {
        code: ExceptionCodeEnum.NotFoundBlobContent,
        displayName: "File Content Missing",
        icon: FileX02,
        color: "error",
        description: "The file content could not be found in storage.",
    },
    [ExceptionCodeEnum.NotFoundJob]: {
        code: ExceptionCodeEnum.NotFoundJob,
        displayName: "Job Not Found",
        icon: FileX02,
        color: "error",
        description: "The processing job could not be found.",
    },
    [ExceptionCodeEnum.NotFoundUser]: {
        code: ExceptionCodeEnum.NotFoundUser,
        displayName: "User Not Found",
        icon: FileX02,
        color: "error",
        description: "The user could not be found.",
    },
    [ExceptionCodeEnum.NotFoundOrganization]: {
        code: ExceptionCodeEnum.NotFoundOrganization,
        displayName: "Organization Not Found",
        icon: FileX02,
        color: "error",
        description: "The organization could not be found.",
    },
    [ExceptionCodeEnum.NotFoundClient]: {
        code: ExceptionCodeEnum.NotFoundClient,
        displayName: "Client Not Found",
        icon: FileX02,
        color: "error",
        description: "The client could not be found.",
    },
    [ExceptionCodeEnum.NotFoundDocumentType]: {
        code: ExceptionCodeEnum.NotFoundDocumentType,
        displayName: "Document Type Not Found",
        icon: FileX02,
        color: "error",
        description: "The document type could not be found.",
    },
    [ExceptionCodeEnum.NotFoundRule]: {
        code: ExceptionCodeEnum.NotFoundRule,
        displayName: "Rule Not Found",
        icon: FileX02,
        color: "error",
        description: "The rule could not be found.",
    },
    [ExceptionCodeEnum.NotFoundWorkflow]: {
        code: ExceptionCodeEnum.NotFoundWorkflow,
        displayName: "Workflow Not Found",
        icon: FileX02,
        color: "error",
        description: "The workflow could not be found.",
    },
    [ExceptionCodeEnum.NotFoundCustomField]: {
        code: ExceptionCodeEnum.NotFoundCustomField,
        displayName: "Custom Field Not Found",
        icon: FileX02,
        color: "error",
        description: "The custom field definition could not be found.",
    },
    [ExceptionCodeEnum.NotFoundException]: {
        code: ExceptionCodeEnum.NotFoundException,
        displayName: "Exception Not Found",
        icon: FileX02,
        color: "error",
        description: "The exception record could not be found.",
    },
    [ExceptionCodeEnum.NotFoundPromptTemplate]: {
        code: ExceptionCodeEnum.NotFoundPromptTemplate,
        displayName: "Template Not Found",
        icon: FileX02,
        color: "error",
        description: "The prompt template could not be found.",
    },
    [ExceptionCodeEnum.NotFoundEntitlement]: {
        code: ExceptionCodeEnum.NotFoundEntitlement,
        displayName: "Entitlement Not Found",
        icon: FileX02,
        color: "error",
        description: "The entitlement could not be found.",
    },

    // ==========================================================================
    // Business Logic Errors (5xx)
    // ==========================================================================
    [ExceptionCodeEnum.BizDuplicateFile]: {
        code: ExceptionCodeEnum.BizDuplicateFile,
        displayName: "Duplicate File",
        icon: Copy01,
        color: "error",
        description: "This file has already been uploaded.",
    },
    [ExceptionCodeEnum.BizDuplicateDocument]: {
        code: ExceptionCodeEnum.BizDuplicateDocument,
        displayName: "Duplicate Document",
        icon: Copy01,
        color: "error",
        description: "This document already exists.",
    },
    [ExceptionCodeEnum.BizQuotaExceeded]: {
        code: ExceptionCodeEnum.BizQuotaExceeded,
        displayName: "Quota Exceeded",
        icon: AlertTriangle,
        color: "error",
        description: "You have reached your monthly page quota limit. Please upgrade your plan or wait until next month.",
    },
    [ExceptionCodeEnum.BizExportBlocked]: {
        code: ExceptionCodeEnum.BizExportBlocked,
        displayName: "Export Blocked",
        icon: XCircle,
        color: "error",
        description: "Export is blocked due to unresolved exceptions. Please resolve all issues before exporting.",
    },
    [ExceptionCodeEnum.BizInvalidStateTransition]: {
        code: ExceptionCodeEnum.BizInvalidStateTransition,
        displayName: "Invalid Status Change",
        icon: AlertCircle,
        color: "error",
        description: "This status change is not allowed.",
    },
    [ExceptionCodeEnum.BizEntityDeleted]: {
        code: ExceptionCodeEnum.BizEntityDeleted,
        displayName: "Item Deleted",
        icon: FileX02,
        color: "gray",
        description: "This item has been deleted.",
    },
    [ExceptionCodeEnum.BizToleranceExceeded]: {
        code: ExceptionCodeEnum.BizToleranceExceeded,
        displayName: "Tolerance Exceeded",
        icon: AlertTriangle,
        color: "error",
        description: "Values exceed the allowed tolerance limits. Please review and adjust.",
    },
    [ExceptionCodeEnum.BizResolutionNoteRequired]: {
        code: ExceptionCodeEnum.BizResolutionNoteRequired,
        displayName: "Resolution Note Required",
        icon: AlertTriangle,
        color: "warning",
        description: "A resolution note is required to proceed with this action.",
    },
    [ExceptionCodeEnum.BizMappingFailure]: {
        code: ExceptionCodeEnum.BizMappingFailure,
        displayName: "Mapping Failed",
        icon: XCircle,
        color: "error",
        description: "The mapping operation could not be completed.",
    },
    [ExceptionCodeEnum.BizMatchFailure]: {
        code: ExceptionCodeEnum.BizMatchFailure,
        displayName: "No Matches Found",
        icon: FileSearch02,
        color: "warning",
        description: "No matching records were found.",
    },
    [ExceptionCodeEnum.BizVendorNotFound]: {
        code: ExceptionCodeEnum.BizVendorNotFound,
        displayName: "Vendor Not Found",
        icon: FileSearch02,
        color: "warning",
        description: "No matching vendor was found in the ERP system.",
    },
    [ExceptionCodeEnum.BizMaxRetriesExceeded]: {
        code: ExceptionCodeEnum.BizMaxRetriesExceeded,
        displayName: "Max Retries Exceeded",
        icon: XCircle,
        color: "error",
        description: "Maximum retry attempts have been exceeded. Please contact support.",
    },

    // ==========================================================================
    // Processing Errors (6xx)
    // ==========================================================================
    [ExceptionCodeEnum.ProcOcrFailed]: {
        code: ExceptionCodeEnum.ProcOcrFailed,
        displayName: "OCR Failed",
        icon: FileSearch02,
        color: "error",
        description: "Text extraction (OCR) failed. Please try uploading the file again.",
    },
    [ExceptionCodeEnum.ProcOcrEmptyResult]: {
        code: ExceptionCodeEnum.ProcOcrEmptyResult,
        displayName: "No Text Extracted",
        icon: FileSearch02,
        color: "error",
        description: "No text could be extracted from this file. Please ensure the file contains readable text.",
    },
    [ExceptionCodeEnum.ProcClassificationFailed]: {
        code: ExceptionCodeEnum.ProcClassificationFailed,
        displayName: "Classification Failed",
        icon: FileX02,
        color: "error",
        description: "Document classification failed. Please try again or contact support.",
    },
    [ExceptionCodeEnum.ProcClassificationEmptyResult]: {
        code: ExceptionCodeEnum.ProcClassificationEmptyResult,
        displayName: "No Documents Found",
        icon: FileX02,
        color: "error",
        description: "No documents could be identified in this file.",
    },
    [ExceptionCodeEnum.ProcClassificationUnknownType]: {
        code: ExceptionCodeEnum.ProcClassificationUnknownType,
        displayName: "Unknown Document Type",
        icon: AlertCircle,
        color: "warning",
        description: "The document type could not be recognized. Please verify the document is a supported type.",
    },
    [ExceptionCodeEnum.ProcParseFailed]: {
        code: ExceptionCodeEnum.ProcParseFailed,
        displayName: "Extraction Failed",
        icon: XCircle,
        color: "error",
        description: "Data extraction failed. Please try again or contact support.",
    },
    [ExceptionCodeEnum.ProcParseEmptyResult]: {
        code: ExceptionCodeEnum.ProcParseEmptyResult,
        displayName: "No Data Extracted",
        icon: XCircle,
        color: "error",
        description: "No data could be extracted from this document.",
    },
    [ExceptionCodeEnum.ProcMatchFailed]: {
        code: ExceptionCodeEnum.ProcMatchFailed,
        displayName: "Matching Failed",
        icon: XCircle,
        color: "error",
        description: "Document matching failed. Please try again.",
    },
    [ExceptionCodeEnum.ProcExtractionUncertain]: {
        code: ExceptionCodeEnum.ProcExtractionUncertain,
        displayName: "Low Confidence",
        icon: AlertTriangle,
        color: "warning",
        description: "Some extracted data has low confidence. Please review and verify.",
    },
    [ExceptionCodeEnum.ProcDocumentCreationFailed]: {
        code: ExceptionCodeEnum.ProcDocumentCreationFailed,
        displayName: "Creation Failed",
        icon: XCircle,
        color: "error",
        description: "Failed to create documents. Please try again or contact support.",
    },
    [ExceptionCodeEnum.ProcClassificationValidationFailed]: {
        code: ExceptionCodeEnum.ProcClassificationValidationFailed,
        displayName: "Classification Validation Failed",
        icon: FileX02,
        color: "error",
        description: "Classification page range validation failed after multiple retries.",
    },
    [ExceptionCodeEnum.ProcPrerequisiteNotMet]: {
        code: ExceptionCodeEnum.ProcPrerequisiteNotMet,
        displayName: "Prerequisite Not Met",
        icon: AlertTriangle,
        color: "error",
        description: "A required processing step has not been completed.",
    },
    [ExceptionCodeEnum.ProcUnsupportedJobType]: {
        code: ExceptionCodeEnum.ProcUnsupportedJobType,
        displayName: "Unsupported Job Type",
        icon: AlertCircle,
        color: "error",
        description: "This job type is not supported.",
    },
    [ExceptionCodeEnum.ProcJobDeserializationFailed]: {
        code: ExceptionCodeEnum.ProcJobDeserializationFailed,
        displayName: "Job Processing Error",
        icon: XCircle,
        color: "error",
        description: "Failed to process the job message. Please try again.",
    },
    [ExceptionCodeEnum.ProcStatusUpdateFailed]: {
        code: ExceptionCodeEnum.ProcStatusUpdateFailed,
        displayName: "Status Update Failed",
        icon: XCircle,
        color: "error",
        description: "Failed to update the status. Please try again.",
    },
    [ExceptionCodeEnum.ProcQuotaCheckFailed]: {
        code: ExceptionCodeEnum.ProcQuotaCheckFailed,
        displayName: "Quota Check Failed",
        icon: AlertCircle,
        color: "error",
        description: "Failed to verify quota. Please try again or contact support.",
    },
    [ExceptionCodeEnum.ProcUsageTrackingFailed]: {
        code: ExceptionCodeEnum.ProcUsageTrackingFailed,
        displayName: "Usage Tracking Failed",
        icon: AlertCircle,
        color: "error",
        description: "Failed to track usage. Please try again or contact support.",
    },
    [ExceptionCodeEnum.ProcCalculatedFieldFailed]: {
        code: ExceptionCodeEnum.ProcCalculatedFieldFailed,
        displayName: "Calculated Field Error",
        icon: AlertTriangle,
        color: "error",
        description: "Failed to evaluate calculated field expression. Please verify the formula.",
    },

    // ==========================================================================
    // Integration Errors (7xx)
    // ==========================================================================
    [ExceptionCodeEnum.IntOpenAiFailed]: {
        code: ExceptionCodeEnum.IntOpenAiFailed,
        displayName: "AI Service Unavailable",
        icon: AlertCircle,
        color: "error",
        description: "AI processing service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntBlobStorageFailed]: {
        code: ExceptionCodeEnum.IntBlobStorageFailed,
        displayName: "Storage Unavailable",
        icon: FileX02,
        color: "error",
        description: "File storage service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntRateLimited]: {
        code: ExceptionCodeEnum.IntRateLimited,
        displayName: "Rate Limited",
        icon: Clock,
        color: "warning",
        description: "Too many requests. Please wait a moment and try again.",
    },
    [ExceptionCodeEnum.IntTimeout]: {
        code: ExceptionCodeEnum.IntTimeout,
        displayName: "Timeout",
        icon: Clock,
        color: "error",
        description: "The request timed out. Please try again.",
    },
    [ExceptionCodeEnum.IntOpenAiInvalidResponse]: {
        code: ExceptionCodeEnum.IntOpenAiInvalidResponse,
        displayName: "AI Response Invalid",
        icon: AlertCircle,
        color: "error",
        description: "The AI service returned an invalid response. Please try again.",
    },
    [ExceptionCodeEnum.IntAirTableFailed]: {
        code: ExceptionCodeEnum.IntAirTableFailed,
        displayName: "AirTable Error",
        icon: AlertCircle,
        color: "error",
        description: "AirTable service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntVercelBlobFailed]: {
        code: ExceptionCodeEnum.IntVercelBlobFailed,
        displayName: "Storage Error",
        icon: FileX02,
        color: "error",
        description: "Blob storage service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntHttpRequestFailed]: {
        code: ExceptionCodeEnum.IntHttpRequestFailed,
        displayName: "Connection Error",
        icon: AlertCircle,
        color: "error",
        description: "An external service request failed. Please try again.",
    },
    [ExceptionCodeEnum.IntServiceError]: {
        code: ExceptionCodeEnum.IntServiceError,
        displayName: "Service Error",
        icon: AlertCircle,
        color: "error",
        description: "An external service returned an error. Please try again later.",
    },
    [ExceptionCodeEnum.IntClerkFailed]: {
        code: ExceptionCodeEnum.IntClerkFailed,
        displayName: "Authentication Service Error",
        icon: AlertCircle,
        color: "error",
        description: "Authentication service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntClerkSyncFailed]: {
        code: ExceptionCodeEnum.IntClerkSyncFailed,
        displayName: "Sync Error",
        icon: AlertCircle,
        color: "error",
        description: "Failed to synchronize account data. Please try again.",
    },

    // ==========================================================================
    // Infrastructure Errors (8xx)
    // ==========================================================================
    [ExceptionCodeEnum.InfDatabaseError]: {
        code: ExceptionCodeEnum.InfDatabaseError,
        displayName: "Database Error",
        icon: XCircle,
        color: "error",
        description: "A database error occurred. Please try again later.",
    },
    [ExceptionCodeEnum.InfServiceUnavailable]: {
        code: ExceptionCodeEnum.InfServiceUnavailable,
        displayName: "Service Unavailable",
        icon: XCircle,
        color: "error",
        description: "The service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.InfQueueError]: {
        code: ExceptionCodeEnum.InfQueueError,
        displayName: "Queue Error",
        icon: XCircle,
        color: "error",
        description: "A queue operation failed. Please try again later.",
    },
    [ExceptionCodeEnum.InfConfigurationMissing]: {
        code: ExceptionCodeEnum.InfConfigurationMissing,
        displayName: "Configuration Error",
        icon: AlertCircle,
        color: "error",
        description: "A system configuration is missing. Please contact support.",
    },
    [ExceptionCodeEnum.InfServiceBusError]: {
        code: ExceptionCodeEnum.InfServiceBusError,
        displayName: "Messaging Error",
        icon: XCircle,
        color: "error",
        description: "The messaging service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.InfSerializationError]: {
        code: ExceptionCodeEnum.InfSerializationError,
        displayName: "Data Processing Error",
        icon: XCircle,
        color: "error",
        description: "Failed to process data. Please try again or contact support.",
    },
    [ExceptionCodeEnum.InfClientSettingsInvalid]: {
        code: ExceptionCodeEnum.InfClientSettingsInvalid,
        displayName: "Settings Error",
        icon: AlertTriangle,
        color: "error",
        description: "Client settings are not properly configured. Please contact support.",
    },
    [ExceptionCodeEnum.InfSchemaInvalid]: {
        code: ExceptionCodeEnum.InfSchemaInvalid,
        displayName: "Schema Error",
        icon: AlertCircle,
        color: "error",
        description: "Schema validation failed. Please contact support.",
    },

    // ==========================================================================
    // Unknown Error
    // ==========================================================================
    [ExceptionCodeEnum.Unknown]: {
        code: ExceptionCodeEnum.Unknown,
        displayName: "Unknown Error",
        icon: AlertTriangle,
        color: "error",
        description: "An unexpected error occurred. Please try again or contact support.",
    },

    // ==========================================================================
    // Legacy Error Codes (for backward compatibility)
    // ==========================================================================
    DUPLICATE_FILE: {
        code: "DUPLICATE_FILE",
        displayName: "Duplicate",
        icon: Copy01,
        color: "error",
        description: "This file is a duplicate of another file",
    },
    OCR_FAILED: {
        code: "OCR_FAILED",
        displayName: "OCR Failed",
        icon: FileSearch02,
        color: "error",
        description: "OCR processing failed or returned poor quality results",
    },
    PDF_CORRUPTED: {
        code: "PDF_CORRUPTED",
        displayName: "Corrupted File",
        icon: FileX02,
        color: "error",
        description: "PDF file is corrupted or unreadable",
    },
    UNSUPPORTED_FORMAT: {
        code: "UNSUPPORTED_FORMAT",
        displayName: "Unsupported Format",
        icon: AlertCircle,
        color: "error",
        description: "File format not supported for processing",
    },
    FILE_TOO_LARGE: {
        code: "FILE_TOO_LARGE",
        displayName: "File Too Large",
        icon: AlertTriangle,
        color: "error",
        description: "File exceeds size limits",
    },
    PROCESSING_ERROR: {
        code: "PROCESSING_ERROR",
        displayName: "Processing Error",
        icon: XCircle,
        color: "error",
        description: "An error occurred while processing this file",
    },
    VALIDATION_ERROR: {
        code: "VALIDATION_ERROR",
        displayName: "Validation Error",
        icon: AlertTriangle,
        color: "error",
        description: "This file failed validation checks",
    },
    TIMEOUT_ERROR: {
        code: "TIMEOUT_ERROR",
        displayName: "Timeout",
        icon: Clock,
        color: "error",
        description: "Processing timed out for this file",
    },
} as const;

/**
 * Get error code definition by code
 */
export function getErrorCodeDefinition(code?: string): ErrorCodeDefinition | null {
    if (!code) return null;
    return ERROR_CODES[code] || null;
}

/**
 * Check if a file has an error based on error code
 */
export function hasErrorCode(errorCode?: string): boolean {
    return !!errorCode && errorCode in ERROR_CODES;
}

/**
 * Get error description (centralized, consistent format)
 */
export function getErrorDescription(errorCode?: string): string {
    const definition = getErrorCodeDefinition(errorCode);
    if (!definition) return "This file requires manual review or has processing errors";

    return definition.description;
}

/**
 * Get display name for error code (fallback to generic text)
 */
export function getErrorDisplayName(errorCode?: string): string {
    const definition = getErrorCodeDefinition(errorCode);
    return definition?.displayName || "Needs Attention";
}

/**
 * Get icon for error code (fallback to AlertTriangle)
 */
export function getErrorIcon(errorCode?: string): ComponentType<any> {
    const definition = getErrorCodeDefinition(errorCode);
    return definition?.icon || AlertTriangle;
}

/**
 * Get color for error code (fallback to warning)
 */
export function getErrorColor(errorCode?: string): "warning" | "error" | "gray" {
    const definition = getErrorCodeDefinition(errorCode);
    return definition?.color || "warning";
}
